import React from 'react';
import { ThreeDBarConfig, ThreeDBarMode, CameraPreset, YScaleType } from '../config/threeDBarTypes';
import { Settings, Grid, Box, Move, Type, Eye } from 'lucide-react';

interface ThreeDBarSettingsProps {
    config: ThreeDBarConfig;
    onChange: (updates: Partial<ThreeDBarConfig>) => void;
    allCols?: string[];
    numericCols?: string[];
    chartConfig?: any;
    onChartConfigChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const ThreeDBarSettings: React.FC<ThreeDBarSettingsProps> = ({
    config,
    onChange,
    allCols = [],
    numericCols = [],
    chartConfig,
    onChartConfigChange
}) => {

    const handleChange = (key: keyof ThreeDBarConfig, value: any) => {
        onChange({ [key]: value });
    };

    return (
        <div className="space-y-6">
            {/* Data Mapping moved to Left Sidebar */}

            {/* Layout moved to Left Sidebar */}

            {/* Camera Section */}
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                    <Move className="w-3.5 h-3.5" /> Camera
                </h4>

                <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-400">Preset View</label>
                    <select
                        value={config.cameraPreset}
                        onChange={(e) => handleChange('cameraPreset', e.target.value as CameraPreset)}
                        className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                    >
                        <option value="isometric">Isometric</option>
                        <option value="top">Top Down</option>
                        <option value="side">Side View</option>
                    </select>
                </div>

                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                        <input
                            type="checkbox"
                            checked={config.enableZoom}
                            onChange={(e) => handleChange('enableZoom', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500"
                        />
                        Zoom
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                        <input
                            type="checkbox"
                            checked={config.enablePan}
                            onChange={(e) => handleChange('enablePan', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500"
                        />
                        Pan
                    </label>
                </div>
            </div>

            {/* Visuals Section */}
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                    <Eye className="w-3.5 h-3.5" /> Visuals
                </h4>

                <div className="space-y-1">
                    <label className="text-xs text-gray-400 block">Opacity: {config.barOpacity}</label>
                    <input
                        type="range"
                        min="0.1"
                        max="1"
                        step="0.1"
                        value={config.barOpacity}
                        onChange={(e) => handleChange('barOpacity', parseFloat(e.target.value))}
                        className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                </div>
            </div>

            {/* Labels Section */}
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                    <Type className="w-3.5 h-3.5" /> Labels
                </h4>

                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                        <input
                            type="checkbox"
                            checked={config.showXLabels}
                            onChange={(e) => handleChange('showXLabels', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500"
                        />
                        X Labels
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                        <input
                            type="checkbox"
                            checked={config.showZLabels}
                            onChange={(e) => handleChange('showZLabels', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500"
                        />
                        Z Labels
                    </label>
                </div>

                <div className="space-y-1">
                    <label className="text-xs text-gray-400 block">X Label Angle: {config.xLabelAngle}°</label>
                    <input
                        type="range"
                        min="0"
                        max="90"
                        step="15"
                        value={config.xLabelAngle}
                        onChange={(e) => handleChange('xLabelAngle', parseInt(e.target.value))}
                        className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                </div>
            </div>

        </div>
    );
};
